import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toPng, toJpeg } from 'html-to-image';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTextLayers } from '@/hooks/useTextLayers';
import axios from 'axios';
import MemeCanvas from '@/components/meme/MemeCanvas';
import Toolbar from '@/components/meme/Toolbar';
import TemplateSelector from '@/components/meme/TemplateSelector';
import ShareModal from '@/components/meme/ShareModal';
import AuthModal from '@/components/auth/AuthModal';
import { Button } from '@/components/ui/button';
import { Download, Share2, Save, ImagePlus, LayoutGrid, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const {
    textLayers, setTextLayers,
    selectedLayerId, setSelectedLayerId,
    selectedLayer,
    updateLayer, addTextLayer, deleteLayer, prefillFirstLayer
  } = useTextLayers();
  const canvasRef = useRef(null);
  
  // Image state
  const [image, setImage] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 600, height: 400 });
  const [templateName, setTemplateName] = useState('');
  
  // Load template from URL params
  useEffect(() => {
    const templateSlug = searchParams.get('template');
    const captionParam = searchParams.get('caption');
    
    if (templateSlug) {
      const loadTemplate = async () => {
        try {
          const response = await axios.get(`${API}/templates/${templateSlug}`);
          const template = response.data;
          setImage(template.url);
          setTemplateName(template.name);
          setShowTemplates(false);
          document.title = `${template.name} - MemeStudio Editor`;
          
          if (captionParam) {
            prefillFirstLayer(captionParam);
          }
          
          axios.post(`${API}/templates/${templateSlug}/use`).catch(() => {});
          
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const maxWidth = 600;
            const ratio = img.height / img.width;
            const width = Math.min(img.width, maxWidth);
            const height = width * ratio;
            setImageSize({ width, height });
          };
          img.src = template.url;
        } catch {
          toast.error('Failed to load template');
        }
      };
      loadTemplate();
    }
  }, [searchParams, prefillFirstLayer]);
  
  const [showTemplates, setShowTemplates] = useState(!searchParams.get('template') && !image);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [exportedImage, setExportedImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Handle image upload
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Maintain aspect ratio with max width of 600px
          const maxWidth = 600;
          const ratio = img.height / img.width;
          const width = Math.min(img.width, maxWidth);
          const height = width * ratio;
          setImageSize({ width, height });
        };
        img.src = event.target.result;
        setImage(event.target.result);
        setShowTemplates(false);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Handle template selection
  const handleTemplateSelect = useCallback((template) => {
    setImage(template.url);
    setShowTemplates(false);
    // Load image to get dimensions
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxWidth = 600;
      const ratio = img.height / img.width;
      const width = Math.min(img.width, maxWidth);
      const height = width * ratio;
      setImageSize({ width, height });
    };
    img.src = template.url;
  }, []);

  // Export meme
  const exportMeme = useCallback(async (format = 'png') => {
    if (!canvasRef.current || !image) {
      toast.error('Please add an image first');
      return null;
    }

    setExporting(true);
    try {
      const exportFn = format === 'png' ? toPng : toJpeg;
      const dataUrl = await exportFn(canvasRef.current, {
        quality: 0.95,
        pixelRatio: 2
      });
      return dataUrl;
    } catch (error) {
      toast.error('Failed to export meme');
      return null;
    } finally {
      setExporting(false);
    }
  }, [image]);

  // Download meme
  const downloadMeme = useCallback(async (format = 'png') => {
    const dataUrl = await exportMeme(format);
    if (dataUrl) {
      saveAs(dataUrl, `meme.${format}`);
      toast.success('Meme downloaded!');
    }
  }, [exportMeme]);

  // Share meme
  const handleShare = useCallback(async () => {
    const dataUrl = await exportMeme('png');
    if (dataUrl) {
      setExportedImage(dataUrl);
      setShowShareModal(true);
    }
  }, [exportMeme]);

  // Save meme to gallery
  const saveMeme = useCallback(async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!image) {
      toast.error('Please add an image first');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/memes`, {
        image_url: image,
        texts: textLayers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meme saved to your gallery!');
    } catch (error) {
      toast.error('Failed to save meme');
    } finally {
      setSaving(false);
    }
  }, [isAuthenticated, image, textLayers, token]);

  return (
    <div className="editor-container">
      {/* Main Canvas Area */}
      <div className="canvas-area pattern-grid">
        {showTemplates ? (
          <TemplateSelector 
            onSelect={handleTemplateSelect}
            onUpload={handleImageUpload}
          />
        ) : (
          <div className="flex flex-col items-center gap-4">
            <MemeCanvas
              ref={canvasRef}
              image={image}
              imageSize={imageSize}
              textLayers={textLayers}
              selectedLayerId={selectedLayerId}
              onSelectLayer={setSelectedLayerId}
              onUpdateLayer={updateLayer}
            />
            
            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => setShowTemplates(true)}
                variant="outline"
                className="border-[#27272a] text-white hover:bg-white/5"
                data-testid="change-image-btn"
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Change Image
              </Button>
              <Button
                onClick={() => downloadMeme('png')}
                disabled={exporting}
                className="bg-[#bef264] text-black hover:bg-[#a3e635]"
                data-testid="download-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download
              </Button>
              <Button
                onClick={handleShare}
                disabled={exporting}
                variant="outline"
                className="border-[#27272a] text-white hover:bg-white/5"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                onClick={saveMeme}
                disabled={saving}
                variant="outline"
                className="border-[#27272a] text-white hover:bg-white/5"
                data-testid="save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {isAuthenticated ? 'Save' : 'Save (Login)'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Toolbar Sidebar */}
      {!showTemplates && (
        <Toolbar
          textLayers={textLayers}
          selectedLayer={selectedLayer}
          selectedLayerId={selectedLayerId}
          onSelectLayer={setSelectedLayerId}
          onUpdateLayer={updateLayer}
          onAddLayer={addTextLayer}
          onDeleteLayer={deleteLayer}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        imageData={exportedImage}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default EditorPage;
